import React from 'react';
import { calculateItemTotal } from '../../domain/calculations';

/**
 * EstimatSummaryRows - Renderar summeringsrader för olika kategorier
 *
 * Hanterar tre typer av summeringar:
 * 1. Byggoffert: Summa + Etablering (med redigerbar %)
 * 2. Inredningsarkitekt: Summering baspaket (2D + FI)
 * 3. Vanliga kategorier: Summa projektering/sidoentreprenörer/fast inredning
 *
 * @param {string} categoryKey - Kategori-nyckel
 * @param {Object} formData - Form data med estimat
 * @param {Function} updateFormData - Funktion för att uppdatera form data
 */
const EstimatSummaryRows = ({ categoryKey, formData, updateFormData }) => {
  // Byggoffert: Summa + Etablering
  if (categoryKey === 'byggoffert') {
    const entries = formData.estimat['byggoffert'] || {};
    let byggoffertTotal = 0;
    let etableringBase = 0;

    Object.entries(entries).forEach(([k, it]) => {
      const itemTotal = calculateItemTotal(it);
      if (itemTotal > 0) {
        byggoffertTotal += itemTotal;
        if (!k.includes('Altan') && !k.includes('Ställning')) {
          etableringBase += itemTotal;
        }
      }
    });
    
    const etableringPercent = Math.max(0, Number(formData?.projectInfo?.etableringPercent ?? 10));
    const etablering = etableringBase * (etableringPercent / 100);
    
    return (
      <>
        <tr className="bg-gray-100 border-t-2 border-gray-300 font-semibold">
          <td colSpan="6" className="px-2 sm:px-4 py-2 sm:py-3 text-sm text-gray-700">Summa byggoffert</td>
          <td className="px-2 sm:px-4 py-2 sm:py-3 text-left text-sm font-bold text-gray-700">
            {byggoffertTotal > 0 ? `${byggoffertTotal.toLocaleString('sv-SE')} kr` : ''}
          </td>
          <td className="p-0 sm:px-4 sm:py-3 w-0 max-w-0 sm:max-w-none sm:w-auto"></td>
        </tr>
        <tr className="bg-gray-50 font-medium">
          <td colSpan="6" className="px-2 sm:px-4 py-2 sm:py-3 text-sm text-gray-700 italic">
            <div className="flex items-center gap-2 flex-wrap">
              <span>Etablering, avfall & admin</span>
              <span className="hidden sm:inline text-gray-500">({etableringPercent} %)</span>
              {/* Mobil: inline %-input */}
              <div className="flex sm:hidden items-center gap-1">
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={Number(formData?.projectInfo?.etableringPercent ?? 10)}
                  onChange={(e) => updateFormData('projectInfo', 'etableringPercent', Number(e.target.value))}
                  onBlur={(e) => updateFormData('projectInfo', 'etableringPercent', Math.max(0, Number(e.target.value)))}
                  className="w-12 px-1 py-0.5 text-xs border border-gray-300 rounded text-right focus:ring-1 focus:ring-leadme-pink focus:border-leadme-pink"
                  aria-label="Justera procent för Etablering, avfall & admin"
                />
                <span className="text-gray-600 text-xs">%</span>
              </div>
            </div>
          </td>
          <td className="px-2 sm:px-4 py-2 sm:py-3 text-left text-sm text-gray-900">
            {etablering > 0 ? `${Math.round(etablering).toLocaleString('sv-SE')} kr` : ''}
          </td>
          <td className="p-0 sm:px-4 sm:py-3 text-sm text-gray-700 w-0 max-w-0 sm:max-w-none sm:w-auto overflow-hidden">
            <div className="hidden sm:flex items-center gap-2 justify-end">
              <label htmlFor="etableringPercent" className="sr-only">Etablering procent</label>
              <input
                id="etableringPercent"
                type="number"
                min="0"
                step="1"
                value={Number(formData?.projectInfo?.etableringPercent ?? 10)}
                onChange={(e) => updateFormData('projectInfo', 'etableringPercent', Number(e.target.value))}
                onBlur={(e) => updateFormData('projectInfo', 'etableringPercent', Math.max(0, Number(e.target.value)))}
                className="w-14 sm:w-20 px-2 py-1 border border-gray-300 rounded text-right focus:ring-1 focus:ring-leadme-pink focus:border-leadme-pink"
                aria-label="Justera procent för Etablering, avfall & admin"
              />
              <span className="text-gray-600">%</span>
            </div>
          </td>
        </tr>
      </>
    );
  }
  
  // Inredningsarkitekt: Summering baspaket
  if (categoryKey === 'inredningsarkitekt') {
    // Matcha både gamla formatet (2D_, FI_) och nya (ritningar2D_, fast2D_)
    const sum2D = Object.entries(formData.estimat['inredningsarkitekt'] || {})
      .filter(([k]) => k.startsWith('2D_') || k.startsWith('ritningar2D_'))
      .reduce((acc, [, v]) => acc + calculateItemTotal(v), 0);

    const sumFI = Object.entries(formData.estimat['inredningsarkitekt'] || {})
      .filter(([k]) => k.startsWith('FI_') || k.startsWith('fast2D_'))
      .reduce((acc, [, v]) => acc + calculateItemTotal(v), 0);

    const sumTotal = sum2D + sumFI;
    
    return (
      <tr className="bg-gray-100 border-t-2 border-gray-300 font-semibold">
        <td colSpan="6" className="px-2 sm:px-4 py-2 sm:py-3 text-sm text-gray-700">
          <span className="relative inline-flex items-center group">
            Summering baspaket
            <button type="button" className="ml-2 text-gray-400 hover:text-gray-600" aria-label="Visa uppdelning" title="Uppdelning">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 100 2 1 1 0 000-2zm-2 7a1 1 0 102 0v-3a1 1 0 10-2 0v3z" clipRule="evenodd" />
              </svg>
            </button>
            <span className="pointer-events-none absolute left-0 -top-8 bg-gray-900 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
              Ritningar 2D: {sum2D.toLocaleString('sv-SE')} kr • Fast inredning 2D: {sumFI.toLocaleString('sv-SE')} kr
            </span>
          </span>
        </td>
        <td className="px-2 sm:px-4 py-2 sm:py-3 text-left text-sm font-bold text-gray-700">
          {sumTotal > 0 ? `${sumTotal.toLocaleString('sv-SE')} kr` : ''}
        </td>
        <td className="p-0 sm:px-4 sm:py-3 w-0 max-w-0 sm:max-w-none sm:w-auto"></td>
      </tr>
    );
  }

  // Vanliga kategorier: projektering, sidoentreprenörer, inkopFastInredning
  if (categoryKey === 'projektering' || categoryKey === 'sidoentreprenorer' || categoryKey === 'inkopFastInredning') {
    const total = Object.values(formData.estimat[categoryKey] || {}).reduce((sum, estimatItem) => {
      return sum + calculateItemTotal(estimatItem);
    }, 0);
    
    let summaText = '';
    if (categoryKey === 'projektering') summaText = 'Summa projektering';
    else if (categoryKey === 'sidoentreprenorer') summaText = 'Summa sidoentreprenörer';
    else if (categoryKey === 'inkopFastInredning') summaText = 'Summa fast inredning';
    
    return (
      <tr className="bg-gray-100 border-t-2 border-gray-300 font-semibold">
        <td colSpan="2" className="px-2 sm:px-4 py-2 sm:py-3 text-sm text-gray-700 whitespace-nowrap">{summaText}</td>
        <td className="px-2 sm:px-4 py-2 sm:py-3 text-center whitespace-nowrap">&nbsp;</td>
        <td className="px-2 sm:px-4 py-2 sm:py-3 text-center whitespace-nowrap">&nbsp;</td>
        <td className="px-2 sm:px-4 py-2 sm:py-3 text-center whitespace-nowrap">&nbsp;</td>
        <td className="px-2 sm:px-4 py-2 sm:py-3 text-center whitespace-nowrap">&nbsp;</td>
        <td className="px-2 sm:px-4 py-2 sm:py-3 text-left text-sm font-bold text-gray-700">
          {total > 0 ? `${total.toLocaleString('sv-SE')} kr` : ''}
        </td>
        <td className="p-0 sm:px-4 sm:py-3 w-0 max-w-0 sm:max-w-none sm:w-auto"></td>
      </tr>
    );
  }
  
  // Ingen summering för andra kategorier
  return null;
};

export default EstimatSummaryRows;

